import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface GlassmorphismContextType {
  isEnabled: boolean;
  toggle: () => void;
}

const GlassmorphismContext = createContext<GlassmorphismContextType | undefined>(undefined);

export const GlassmorphismProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // LocalStorageから初期値を取得（デフォルトは有効）
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('glassmorphism');
    return stored === null ? true : stored === 'true';
  });

  // グラスモーフィズムの切り替え
  const toggle = () => {
    setIsEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('glassmorphism', String(newValue));

      // body要素にclassを付与/削除
      if (newValue) {
        document.body.classList.add('glassmorphism-enabled');
      } else {
        document.body.classList.remove('glassmorphism-enabled');
      }

      return newValue;
    });
  };

  // 初期化時とisEnabled変更時にbody要素のclassを更新
  useEffect(() => {
    if (isEnabled) {
      document.body.classList.add('glassmorphism-enabled');
    } else {
      document.body.classList.remove('glassmorphism-enabled');
    }
  }, [isEnabled]);

  // ページ読み込み時に初期状態を設定
  useEffect(() => {
    // 少し遅延させてDOMが準備できるのを待つ
    const timer = setTimeout(() => {
      if (isEnabled) {
        document.body.classList.add('glassmorphism-enabled');
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  return (
    <GlassmorphismContext.Provider value={{ isEnabled, toggle }}>
      {children}
    </GlassmorphismContext.Provider>
  );
};

// カスタムフック
export const useGlassmorphism = () => {
  const context = useContext(GlassmorphismContext);
  if (!context) {
    throw new Error('useGlassmorphism must be used within GlassmorphismProvider');
  }
  return context;
};